import { Button, keyframes } from '@mui/material';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { Business_Profile_Enum } from '@kheops/graphql';
import useMakeAnOffer from '../hooks/useMakeAnOffer';
import useOpenable from '../../hooks/useOpenable';
import ContractConditionsDialog from '../../common/components/ContractConditionsDialog';
import ContractsUtils from '../../common/utils/contracts.utils';
import { currentContextAtom } from '../../state';
import { ContractOfferInfo, ContractConditions } from '../state';

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.6);
  }
  60% {
    opacity: 1;
    transform: translateY(-2px) scale(1.06);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export interface MakeAnOfferProps {
  contract: ContractOfferInfo;
  channel: GroupChannel;
}

export default function MakeAnOfferButton({ contract, channel }: MakeAnOfferProps): React.JSX.Element {
  const { t } = useTranslation(['contracts']);
  const context = useAtomValue(currentContextAtom);
  const makeAnOffer = useMakeAnOffer();
  const location = useLocation();
  const { open: openContractConditionsDialog, close: closeContractConditionsDialog, isOpen: isContractConditionsDialogOpen } = useOpenable();
  const conditions = useMemo(() => {
    if (!contract) {
      return;
    }

    return ContractsUtils.getDefaultContractConditions(contract, context);
  }, [contract, context]);

  useEffect(() => {
    if (location.state?.newCustomPriceListId || location.state?.newSubCatalogId) {
      openContractConditionsDialog();
    }
  }, [location]);

  const handleContractOfferDialogClose = (response?: ContractConditions): void => {
    if (response) {
      makeAnOffer(channel, response, contract.id);
    }

    closeContractConditionsDialog();
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddBusinessOutlinedIcon />}
        onClick={openContractConditionsDialog}
        sx={{
          animation: `${popIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
          transformOrigin: 'center bottom',
        }}
      >
        {t('contracts:make_an_offer')}
      </Button>
      {isContractConditionsDialogOpen && !!conditions && (
        <ContractConditionsDialog
          onClose={(response) => handleContractOfferDialogClose(response)}
          open={isContractConditionsDialogOpen}
          title={t('contracts:make_an_offer')}
          description={t(`contracts:${context.realm === Business_Profile_Enum.Buyer ? 'offer_dialog_description_as_buyer' : 'offer_dialog_description_as_supplier'}`)}
          submitLabel={t('contracts:offer')}
          contractId={contract.id}
          conditions={conditions}
          commissionRate={context.commissionRate}
          firstOffer
        />
      )}
    </>
  );
}
